import { Component } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FeatureFlag } from '@app/enums';
import { PendingPayrollStatus } from '@app/interfaces/pending-payroll-status.interface';
import { Employee } from '@app/models/employee/employee.model';
import { CompanyAdditionalIncome } from '@app/models/payroll/company-additional-income.model';
import { EmployeeAdditionalIncome } from '@app/models/payroll/employee-additional-income.model';
import { AuthService, NotifyService, PayrollService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { ALL_PROVINCES, Province } from '@app/types/province';

interface EmployeeWithEmployeeAdditionalIncome extends Employee {
    employeeAdditionalIncome?: EmployeeAdditionalIncome;
}

@Component({
    templateUrl: './additional-income-show.template.html',
    styleUrls: ['./additional-income-show.style.scss'],
})
export class CompanyAdditionalIncomeShowView {
    isLoading = true;
    employees: EmployeeWithEmployeeAdditionalIncome[] = [];
    employeeAdditionalIncomes: EmployeeAdditionalIncome[] = [];
    companyAdditionalIncome!: CompanyAdditionalIncome;
    pendingPayrollExists = false;
    editModalTitle = 'Select Income Type';
    hasQcPayrollFeature = false;
    isOpenEmployeeAdditionalIncome = false;
    selectedProvince: Province = 'ON';
    provinces = ALL_PROVINCES;

    constructor(
        private route: ActivatedRoute,
        private auth: AuthService,
        private notify: NotifyService,
        private payrollService: PayrollService,
        private features: FeatureService
    ) {
        this.loadData();
    }

    async onSave(
        form: NgForm,
        employee: EmployeeWithEmployeeAdditionalIncome | null,
        employeeAdditionalIncome: EmployeeAdditionalIncome | null
    ): Promise<void> {
        if (!form.valid || !employee || !employeeAdditionalIncome) {
            return;
        }

        try {
            this.isLoading = true;
            await employeeAdditionalIncome.save();
            employee.employeeAdditionalIncome = employeeAdditionalIncome;
            employeeAdditionalIncome.param('employee', employee.id);

            this.notify.info('payroll.additional-incomes.saved');
            this.isOpenEmployeeAdditionalIncome = false;
        } catch (error) {
            this.notify.error('payroll.additional-incomes.couldNotSave');
        } finally {
            this.isLoading = false;
        }
    }

    async onRemove(employeeAdditionalIncome: EmployeeAdditionalIncome | undefined): Promise<void> {
        if (!employeeAdditionalIncome) {
            return;
        }

        this.isLoading = true;

        employeeAdditionalIncome.active = false;
        employeeAdditionalIncome.amount = 0;
        try {
            await employeeAdditionalIncome.save();
            this.notify.info('payroll.additional-incomes.removed');
        } catch (error) {
            this.notify.error('payroll.additional-incomes.couldNotRemove');
        } finally {
            this.isLoading = false;
        }
    }

    onCancel(employeeAdditionalIncome: EmployeeAdditionalIncome | null): void {
        if (employeeAdditionalIncome) {
            employeeAdditionalIncome.revert();
        }
        this.isOpenEmployeeAdditionalIncome = false;
    }

    onSubmit(): void {}

    setEditModalTitle(employee: EmployeeWithEmployeeAdditionalIncome): void {
        this.editModalTitle = `"${this.companyAdditionalIncome.displayName}" for ${employee.fullName}`;
    }

    private async loadData(): Promise<void> {
        this.companyAdditionalIncome = this.route.snapshot.data.models.get('companyAdditionalIncome');
        this.checkPendingPayrollStatus();
        const [employees] = await Employee.param('company', this.auth.company?.id)
            .where('onPayroll', 1)
            .orderBy('lastName', 'ASC')
            .whereIn('status', ['active'])
            .limit(0)
            .get();

        this.employees = employees.map(
            (employee: EmployeeWithEmployeeAdditionalIncome): EmployeeWithEmployeeAdditionalIncome => {
                employee.employeeAdditionalIncome = this.companyAdditionalIncome.employeeAdditionalIncomes.find(
                    ({ prEmployeeId }) => prEmployeeId === employee.prEmployeeId
                );

                if (employee.employeeAdditionalIncome) {
                    employee.employeeAdditionalIncome.param('employee', employee.id);
                }

                return employee;
            }
        );

        this.hasQcPayrollFeature = await this.features.has(FeatureFlag.qcPayroll);

        this.isLoading = false;
        this.isOpenEmployeeAdditionalIncome = false;
    }

    private checkPendingPayrollStatus(): void {
        this.payrollService
            .getPendingPayrollStatus()
            .then(({ exists }: PendingPayrollStatus) => (this.pendingPayrollExists = exists));
    }
}
